import { InstantSearch, Configure, Index } from 'react-instantsearch-dom'
import { connectStateResults, connectAutoComplete } from 'react-instantsearch/connectors'
import { Component, h } from 'preact'
import algoliasearch from 'algoliasearch'
import globals from '../../../utils/globals'
import { SEARCH_TOKEN_URL } from '../../../libs/config'
import getWithInMemoryCache from '../../../utils/getWithInMemoryCache'
import AlgoliaSearch from './AlgoliaSearch'
import AlgoliaSearchForControl from './AlgoliaSearchForControl'
import { Hit } from 'js/src/preact-components/algolia/components'
import { SearchBox } from './SearchBox'
import Icon from '../../assets'
import Button from '../../ui-components/Button'
import Phrase from '../../../libs/translationsUtils/phrase'
const SearchWithAutoComplete = connectAutoComplete(connectStateResults(AlgoliaSearch))
const SearchWithAutoCompleteForControl = connectAutoComplete(connectStateResults(AlgoliaSearchForControl))

declare global {
    interface Window {
      aa: (command: string, data: {
        applicationID: string,
        apiKey: string
      } | {
        objectID: string,
        position: number,
        queryID: string
      }) => void
    }
}

interface Props {
  isControl?: boolean,
  isFederatedSearch?: boolean,
  isFederatedSalesforceSearch?: boolean,
  isFederatedSkilljarSearch?: boolean,
  isFederatedFreshdeskSearch?: boolean,
  isUserGuest?: boolean,
  searchPlaceholder?: string,
  callback?: (hit: Hit) => void,
  exclude?: Array<number>,
  submitIconType?: string
  category?:number,
  isParentCategory?:boolean,
  searchByContentType?: Array<string>,
  displayFilters?: boolean
}

interface State {
  searching: boolean,
  searchById: boolean,
  searchByContentType?: Array<string>,
  finalInput: string,
  searchClient?: algoliasearch.Client,
  listIndexes: Array<string>,
  isFiltersOverlay: boolean
}

interface SearchQuery {
  q: string,
  contentType?: string
}

class Search extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
      searching: false,
      searchById: false,
      searchByContentType: props.searchByContentType,
      finalInput: '',
      searchClient: undefined,
      listIndexes: [],
      isFiltersOverlay: false
    }
    this.updateSearch = this.updateSearch.bind(this)
    this.filtersOverlayHandler = this.filtersOverlayHandler.bind(this)
    this.revertFilters = this.revertFilters.bind(this)
  }

  updateSearch (searchTerm: SearchQuery, next: () => void) {
    this.setState({
      searching: searchTerm.q !== '',
      searchById: searchTerm.q.match(/^[0-9]+$/) !== null,
      finalInput: searchTerm.q,
      searchByContentType: searchTerm.contentType ? [searchTerm.contentType] : this.props.searchByContentType
    }, next)
  }

  async componentDidMount () {
    const data: {client_id: string, token: string, basicToken: string} = await getWithInMemoryCache(SEARCH_TOKEN_URL, {})
    const searchClient = algoliasearch(data.client_id, data.token)
    const basicClient = algoliasearch(data.client_id, data.basicToken)
    const communityId = globals('communityId')

    this.setState({
      searchClient: searchClient
    }, () => {
      if (typeof window.aa === 'function') {
        window.aa('init', {
          applicationID: data.client_id,
          apiKey: data.token
        })
      }
    })

    try {
      const indexes: {
        items: Array<{
          name: string
        }>
      } = await basicClient.listIndexes()
      const indexNames: Array<string> = indexes.items.map((item => item.name))

      this.setState({
        listIndexes: indexNames
      })
    } catch (e) {
      this.setState({
        listIndexes: [`${communityId}`, `${communityId}-zendesk`, `${communityId}-salesforce`, `${communityId}-skilljar`, `${communityId}-freshdesk`]
      })
    }
  }

  resetFilters (e: Event) {
    e.stopPropagation()
    e.preventDefault()

    window.dispatchEvent(new CustomEvent('resetFilters'))
  }

  revertFilters (e: Event) {
    e.stopPropagation()
    e.preventDefault()

    window.dispatchEvent(new CustomEvent('revertFilters'))
    this.filtersOverlayHandler(e, false)
  }

  filtersOverlayHandler (e: Event, value: boolean) {
    e.stopPropagation()
    e.preventDefault()

    window.dispatchEvent(new CustomEvent('filter', {
      detail: {
        display: value
      }
    }))

    if (value) {
      this.setState({
        isFiltersOverlay: value
      })
    } else {
      /*
        Items that should be in the filters are added via Portal component
        having setTimeout here is making state change async and give time to filter component to unmount
      */
      window.setTimeout(() => {
        this.setState({
          isFiltersOverlay: !this.state.isFiltersOverlay
        })
      }, 0)
    }

    const bodyEl = document.querySelector('body')

    if (bodyEl) {
      if (bodyEl.style.overflow === 'hidden') {
        bodyEl.style.overflow = 'visible'
      } else {
        bodyEl.style.overflow = 'hidden'
      }
    }
  }

  render (props: Props, state: State) {
    if (!state.searchClient) {
      return <SearchBox
        submitIconType={props.submitIconType || ''}
      />
    }
    const communityId = globals('communityId')
    return (
      <div className='search-and-filter-wrapper'>
        <PureSearch
          {...props}
          searchClient={state.searchClient}
          updateSearch={this.updateSearch}
          searchById={state.searchById}
          searchByContentType={state.searchByContentType}
          finalInput={state.finalInput}
          categoryId={props.category}
          searching={state.searching}
          communityId={typeof communityId === 'string' ? communityId : ''}
          submitIconType={props.submitIconType || ''}
          listIndexes={state.listIndexes}
        />
        {
          props.displayFilters ? (
            <button className='instant-search__filter'
                    onClick={(e: Event) => this.filtersOverlayHandler(e, true)}>
              <Icon width={18} height={12} color='#6B707E' icon='filter' />
            </button>
          ) : null
        }
        {
          state.isFiltersOverlay ? (
            <div className={`instant-search__results-wrapper is-hidden-L ${!state.isFiltersOverlay ? '' : ''}`}>
              <div
                className={`instant-search__results-wrapper-filter ${!state.isFiltersOverlay
                  ? 'instant-search__results-wrapper-filter--hide'
                  : ''}`}>
                <div className='instant-search__results-wrapper-filter-header'>
                  <Icon icon='close' onClick={this.revertFilters}/>
                  <h3
                    className='instant-search__results-wrapper-filter-header-heading'>
                    <Phrase p='advanced.search.filters'/>
                  </h3>
                  <button
                    onClick={this.resetFilters}
                    className='instant-search__results-wrapper-filter-header-btn'>
                    <Phrase m='Common' p='advanced.search.filter.clear.all'/>
                  </button>
                </div>
                {
                  /*
                  * Filters can be added via Portal component from real place of filters.
                  * Since Search does not include them it does not care about which items needs to be display in which order,
                  * all those decisions can be done on filter component
                  * */
                }
                <div className='instant-search__results-wrapper-filters'>
                </div>
                <div
                  className='instant-search__results-wrapper-filter-show-more-wrapper'>
                  <Button
                    cta
                    class='instant-search__results-wrapper-filter-show-more'
                    onClick={(e: Event) => this.filtersOverlayHandler(e, false)}
                  >
                    <Phrase p='show.results'/>
                  </Button>
                </div>
              </div>
            </div>
          ) : null
        }
      </div>
    )
  }
}

interface PureProps extends Props {
  communityId: string,
  searching: boolean,
  searchById: boolean,
  searchByContentType?: Array<string>,
  finalInput: string,
  searchClient: algoliasearch,
  updateSearch: (value: SearchQuery, cb: () => void) => void,
  categoryId?: number,
  groupId?: number,
  listIndexes: Array<string>
}

const getFilters = (props: PureProps) => {
  if (props.categoryId) {
    if (props.isParentCategory) {
      return `category:${props.categoryId}`
    } else {
      return `forum:${props.categoryId}`
    }
  } else if (props.groupId) {
    return `forum:${props.groupId}`
  }

  if (props.searchByContentType) {
   return props.searchByContentType.map(value => {
      return `content_type:${value}`
    }).join(' OR ')
  }
  return undefined
}

const PureSearch = (props: PureProps) => <InstantSearch
  searchClient={props.searchClient}
  indexName={props.communityId}
>
  {
    props.listIndexes.includes(props.communityId) ? (
      <Index indexName={props.communityId}>
        <Configure
          hitsPerPage={5}
          filters={getFilters(props)}
          clickAnalytics={props.searching}
          analyticsTags={props.isControl ? ['control'] : ['destination', 'live']}
          restrictSearchableAttributes={props.searchById ? ['id'] : ['title', 'first_post', 'answer', 'reply', 'tags', 'forum_title', 'category_title']} />
      </Index>
    ) : null
  }

  {
    props.isFederatedSalesforceSearch && props.listIndexes.includes(`${props.communityId}-salesforce`) ? (
      <Index indexName={`${props.communityId}-salesforce`}>
        <Configure
          hitsPerPage={3}
          filters={getFilters(props)}
          clickAnalytics={props.searching}
          analyticsTags={props.isControl ? ['control'] : ['destination', 'live']}
        />
      </Index>
    ) : null
  }

{
    props.isFederatedSkilljarSearch && props.listIndexes.includes(`${props.communityId}-skilljar`) ? (
      <Index indexName={`${props.communityId}-skilljar`}>
        <Configure
          hitsPerPage={3}
          clickAnalytics={props.searching}
        />
      </Index>
    ) : null
  }

{
    props.isFederatedFreshdeskSearch && props.listIndexes.includes(`${props.communityId}-freshdesk`) ? (
      <Index indexName={`${props.communityId}-freshdesk`}>
        <Configure
          hitsPerPage={3}
          clickAnalytics={props.searching}
        />
      </Index>
    ) : null
  }

  {
    props.isFederatedSearch && props.listIndexes.includes(`${props.communityId}-zendesk`) ? (
      <Index indexName={`${props.communityId}-zendesk`}>
        <Configure
          hitsPerPage={3}
          clickAnalytics={props.searching} />
      </Index>
    ) : null
  }

  {
    props.isControl ? (
      <SearchWithAutoCompleteForControl
        updateSearch={(value: SearchQuery, next: () => void) => props.updateSearch(value, next)}
        searchById={props.searchById}
        finalInput={props.finalInput}
        placeholder={props.searchPlaceholder}
        callback={props.callback}
        exclude={props.exclude}
        submitIconType={props.submitIconType || ''}
      />
    ) : (
      <SearchWithAutoComplete
        updateSearch={(value: SearchQuery, next: () => void) => props.updateSearch(value, next)}
        callback={props.callback}
        indexName={props.communityId}
        isFederatedSearch={props.isFederatedSearch}
        isFederatedSalesforceSearch={props.isFederatedSalesforceSearch}
        isFederatedSkilljarSearch={props.isFederatedSkilljarSearch}
        isFederatedFreshdeskSearch={props.isFederatedFreshdeskSearch}
      />
    )
  }
</InstantSearch>

export { PureSearch, SearchQuery }
export default Search
